import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  Typography,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  IconButton,
  Grid,
  Select,
  MenuItem,
  Checkbox,
  FormControl,
  FormControlLabel,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import { format, isValid, parse, differenceInDays } from 'date-fns';
import { Fields } from '../../components';
import axios from '../../../../../../../../../../api/axios';
import './lease-form.scss';
import { Cancel } from '@mui/icons-material';
import { Input } from 'reactstrap';

const formatDateForDisplay = (dateString) => {
  if (!dateString) return 'N/A';
  const cleanDateStr = dateString.split('T')[0]; // Remove time part if present
  const match = cleanDateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : 'Invalid Date';
};

const getOrdinal = (day) => {
  if (day > 3 && day < 21) return `${day}th`;
  switch (day % 10) {
    case 1: return `${day}st`;
    case 2: return `${day}nd`;
    case 3: return `${day}rd`;
    default: return `${day}th`;
  }
};

const formatDateWithOrdinal = (dateString) => {
  if (!dateString) return 'Click to select date';
  const cleanDateStr = dateString.split('T')[0];
  const date = parse(cleanDateStr, 'yyyy-MM-dd', new Date());
  if (!isValid(date)) return 'Click to select date';
  const month = format(date, 'MMMM');
  const dayWithOrdinal = getOrdinal(date.getDate());
  const year = format(date, 'yyyy');
  return `${month} ${dayWithOrdinal}, ${year}`;
};

// Utility fee rows shown when the property does NOT include the utility in rent.
const UTILITY_ROWS = [
  { key: 'heat', feeField: 'heat_fee', label: 'Heat' },
  { key: 'water_waste', feeField: 'water_waste_fee', label: 'Water/Waste' },
  { key: 'electricity', feeField: 'electricity_fee', label: 'Electricity' },
];

const Tenants = ({
  formik,
  fieldName = 'tenants',
  options = {},
}) => {
  const [editingIndex, setEditingIndex] = useState(null);

  const {
    minItems = 1,
    maxItems = 4,
    emptyText = 'No tenants added',
    paperProps = { variant: 'outlined', className: 'unit-details-table' },
  } = options;

  const tenants = formik.values[fieldName] || [];

  const handleAddTenant = useCallback(() => {
    if (tenants.length >= maxItems) return;
    const newTenant = { name: '' };
    const updatedTenants = [...tenants, newTenant];
    formik.setFieldValue(fieldName, updatedTenants);
    setEditingIndex(updatedTenants.length - 1);
  }, [tenants.length, maxItems, fieldName, formik]);

  const handleRemoveTenant = useCallback((index) => {
    if (tenants.length <= minItems) return;
    const updatedTenants = tenants.filter((_, i) => i !== index);
    formik.setFieldValue(fieldName, updatedTenants);
    if (editingIndex === index) {
      setEditingIndex(null);
    } else if (editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  }, [tenants, minItems, fieldName, formik, editingIndex]);

  const handleTenantNameChange = useCallback((index, value) => {
    const updatedTenants = [...tenants];
    updatedTenants[index] = { ...updatedTenants[index], name: value };
    formik.setFieldValue(fieldName, updatedTenants);
  }, [tenants, fieldName, formik]);

  const startEditing = useCallback((index) => {
    setEditingIndex(index);
  }, []);

  const stopEditing = useCallback(() => {
    setEditingIndex(null);
  }, []);

  useEffect(() => {
    if (tenants.length < minItems) {
      const emptyTenants = Array(minItems).fill().map(() => ({ name: '' }));
      formik.setFieldValue(fieldName, emptyTenants);
    }
  }, [tenants.length, minItems, fieldName, formik]);

  const renderTenantName = (tenant, index) => {
    const isEditing = editingIndex === index;
    if (isEditing) {
      return (
        <TextField
          fullWidth
          variant="standard"
          value={tenant.name || ''}
          onChange={(e) => handleTenantNameChange(index, e.target.value)}
          onBlur={stopEditing}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              stopEditing();
            }
          }}
          autoFocus
          placeholder="Enter tenant name"
        />
      );
    }
    return (
      <Box
        onClick={() => startEditing(index)}
        sx={{
          cursor: 'pointer',
          minHeight: '36px',
          display: 'flex',
          alignItems: 'center',
          flexGrow: 1,
          color: tenant.name ? 'inherit' : '#aaa',
        }}
      >
        {tenant.name || 'Click to add tenant name'}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            startEditing(index);
          }}
          sx={{ ml: 1 }}
        >
          <EditIcon fontSize="small" color='error' />
        </IconButton>
      </Box>
    );
  };

  const renderActionButtons = (index) => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', ml: 2 }}>
        {tenants.length > minItems && (
          <IconButton
            size="small"
            onClick={() => handleRemoveTenant(index)}
            color="error"
            sx={{ mr: 1 }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
        {tenants.length < maxItems && index === 0 && (
          <IconButton
            size="small"
            onClick={handleAddTenant}
            color="primary"
          >
            <AddIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  };

  const renderEmptyState = () => {
    return (
      <TableRow>
        <TableCell colSpan={2} align="center">
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 2,
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {emptyText}
            </Typography>
            {tenants.length < maxItems && (
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddTenant}
                variant="outlined"
                size="small"
              >
                Add Tenant
              </Button>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box className="manual-tenant-manager">
      <Paper {...paperProps}>
        <Table size="small">
          <TableBody>
            {tenants.length > 0 ? (
              tenants.map((tenant, index) => (
                <TableRow key={index}>
                  <TableCell className="label">Full Name</TableCell>
                  <TableCell>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}>
                      {renderTenantName(tenant, index)}
                      {renderActionButtons(index)}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              renderEmptyState()
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

const LeaseDetails = ({ formik, incentiveOptions = [] }) => {
  const [editingField, setEditingField] = useState(null);
  const [editingPetIndex, setEditingPetIndex] = useState(null);
  const [tempPet, setTempPet] = useState({ category: '', total_fee: '', no_of_pets: '1' });
  const [editingParkingIndex, setEditingParkingIndex] = useState(null);
  const [tempParking, setTempParking] = useState({ parking_type: '', total_fee: '', no_of_slots: '1' });
  const [editingStorageIndex, setEditingStorageIndex] = useState(null);
  const [tempStorage, setTempStorage] = useState({ total_fee: '', no_of_slots: '1' });
  const [customIncentiveValue, setCustomIncentiveValue] = useState('');
  const [isAmountToBeChargedManuallyEdited, setIsAmountToBeChargedManuallyEdited] = useState(false);
  const [showSaveMessage, setShowSaveMessage] = useState({ pets: false, parking: false, storage: false });

  const leaseDetails = formik.values.lease_details || {};
  const pets = leaseDetails.pets || [];
  const parking = leaseDetails.parking || [];
  const storage = leaseDetails.storage || [];

  // Function to check if an object has all empty/undefined fields
  const isObjectEmpty = (obj) => {
    return Object.values(obj).every(
      (value) => value === '' || value === undefined || value === null
    );
  };

  // Check for empty objects in pets, parking, storage
  useEffect(() => {
    const checkEmptyArrays = () => {
      const petsHasEmpty = pets.length > 0 && pets.some((pet) => isObjectEmpty(pet));
      const parkingHasEmpty = parking.length > 0 && parking.some((parkingItem) => isObjectEmpty(parkingItem));
      const storageHasEmpty = storage.length > 0 && storage.some((storageItem) => isObjectEmpty(storageItem));

      setShowSaveMessage({
        pets: petsHasEmpty,
        parking: parkingHasEmpty,
        storage: storageHasEmpty,
      });
    };

    checkEmptyArrays();
  }, [pets, parking, storage]);

  const parseDate = (dateString) => {
    if (!dateString) return '';
    const cleanDateString = dateString.includes('T') ? dateString.split('T')[0] : dateString;
    const date = parse(cleanDateString, 'yyyy-MM-dd', new Date());
    return isValid(date) ? cleanDateString : '';
  };

  const formatDateForStorage = (date) => {
    if (!date) return '';
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    const parsedDate = new Date(date);
    return isValid(parsedDate) ? format(parsedDate, 'yyyy-MM-dd') : '';
  };

  const shouldDisableDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const getNextParkingType = () => {
    const lastParkingType = parking.length > 0 ? parking[parking.length - 1].parking_type : null;
    return lastParkingType === 'Underground' ? 'Surface Stall' : 'Underground';
  };

  const calculatePetFees = () => {
    return pets.reduce((total, pet) => {
      const fee = parseFloat(pet.total_fee || 0);
      return total + fee;
    }, 0);
  };

  const calculateParkingFees = () => {
    return parking.reduce((total, parkingItem) => {
      const fee = parseFloat(parkingItem.total_fee || 0);
      return total + fee;
    }, 0);
  };

  const calculateStorageFees = () => {
    return storage.reduce((total, storageItem) => {
      const fee = parseFloat(storageItem.total_fee || 0);
      return total + fee;
    }, 0);
  };

  const calculateUtilityFees = () => {
    const u = leaseDetails.utilities || {};
    return (
      parseFloat(u.heat_fee || 0) +
      parseFloat(u.water_waste_fee || 0) +
      parseFloat(u.electricity_fee || 0)
    );
  };

  const calculateTotalMonthlyRent = () => {
    const basicRent = parseFloat(leaseDetails.basic_rent || 0);
    const petFees = calculatePetFees();
    const parkingFees = calculateParkingFees();
    const storageFees = calculateStorageFees();
    const telusCharges = leaseDetails.telus?.included ? 0 : parseFloat(leaseDetails.telus?.charges || 0);
    const utilityFees = calculateUtilityFees();

    return basicRent + petFees + parkingFees + storageFees + telusCharges + utilityFees;
  };

  const calculateTotalRent = () => {
    const formatCurrency = (value) => {
      const num = parseFloat(value);
      if (isNaN(num)) return '-';
      return `$${num.toFixed(2)}`;
    };
    const total = calculateTotalMonthlyRent();
    return formatCurrency(total);
  };

  const proRationData = useMemo(() => {
    const { date_of_early_move_in } = leaseDetails.pro_ration || {};
    const { start_date } = leaseDetails;

    if (!start_date || !date_of_early_move_in) {
      return { amount_applicable: 0, amount_to_be_charged: 0 };
    }

    const startDate = parse(parseDate(start_date), 'yyyy-MM-dd', new Date());
    const earlyMoveInDate = parse(parseDate(date_of_early_move_in), 'yyyy-MM-dd', new Date());
    const totalMonthlyRent = calculateTotalMonthlyRent();

    if (!isValid(startDate) || !isValid(earlyMoveInDate) || totalMonthlyRent <= 0) {
      return { amount_applicable: 0, amount_to_be_charged: 0 };
    }

    const days = differenceInDays(startDate, earlyMoveInDate);
    if (days <= 0) {
      return { amount_applicable: 0, amount_to_be_charged: 0 };
    }

    const daysInMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate();
    const perDayRent = totalMonthlyRent / daysInMonth;
    const proratedAmount = parseFloat((days * perDayRent).toFixed(2));

    return {
      amount_applicable: proratedAmount,
      amount_to_be_charged: proratedAmount,
    };
  }, [
    leaseDetails.pro_ration?.date_of_early_move_in,
    leaseDetails.start_date,
    leaseDetails.basic_rent,
    pets,
    parking,
    storage,
    leaseDetails.telus?.included,
    leaseDetails.telus?.charges,
    leaseDetails.utilities?.heat_fee,
    leaseDetails.utilities?.water_waste_fee,
    leaseDetails.utilities?.electricity_fee,
  ]);

  useEffect(() => {
    const currentApplicable = leaseDetails.pro_ration?.amount_applicable;
    const currentToBeCharged = leaseDetails.pro_ration?.amount_to_be_charged;

    if (proRationData.amount_applicable !== false && currentApplicable !== proRationData.amount_applicable) {
      formik.setFieldValue('lease_details.pro_ration.amount_applicable', proRationData.amount_applicable);
    } else if (proRationData.amount_applicable === false && currentApplicable !== false) {
      formik.setFieldValue('lease_details.pro_ration.amount_applicable', false);
    }

    if (!isAmountToBeChargedManuallyEdited &&
      currentToBeCharged !== proRationData.amount_to_be_charged) {
      formik.setFieldValue('lease_details.pro_ration.amount_to_be_charged', proRationData.amount_to_be_charged);
    }
  }, [
    proRationData.amount_applicable,
    proRationData.amount_to_be_charged,
    isAmountToBeChargedManuallyEdited,
    formik,
    leaseDetails.pro_ration?.amount_applicable,
    leaseDetails.pro_ration?.amount_to_be_charged,
  ]);

  // On mount, treat a saved "N/A" (early move-in set but charge 0) as a manual
  // edit so the auto-recompute doesn't re-enable it when returning to this step.
  useEffect(() => {
    const { date_of_early_move_in, amount_to_be_charged } = leaseDetails.pro_ration || {};
    if (date_of_early_move_in && Number(amount_to_be_charged) === 0) {
      setIsAmountToBeChargedManuallyEdited(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNoCharge = useCallback(() => {
    formik.setFieldValue('lease_details.pro_ration.amount_to_be_charged', 0);
    setIsAmountToBeChargedManuallyEdited(true);
  }, [formik]);

  const addPet = useCallback(() => {
    if (editingPetIndex !== null && tempPet) {
      const updatedPetsWithCurrentChanges = [...pets];
      updatedPetsWithCurrentChanges[editingPetIndex] = { ...tempPet };
      const newPet = { category: '', total_fee: '', no_of_pets: '1' };
      const finalUpdatedPets = [...updatedPetsWithCurrentChanges, newPet];
      formik.setFieldValue('lease_details.pets', finalUpdatedPets);
      setEditingPetIndex(finalUpdatedPets.length - 1);
      setTempPet(newPet);
    } else {
      const newPet = { category: '', total_fee: '', no_of_pets: '1' };
      const updatedPets = [...pets, newPet];
      formik.setFieldValue('lease_details.pets', updatedPets);
      setEditingPetIndex(updatedPets.length - 1);
      setTempPet(newPet);
    }
  }, [pets, formik, editingPetIndex, tempPet]);

  const removePet = useCallback((index) => {
    const updatedPets = pets.filter((_, i) => i !== index);
    formik.setFieldValue('lease_details.pets', updatedPets);
    setIsAmountToBeChargedManuallyEdited(false);
    if (editingPetIndex === index) {
      setEditingPetIndex(null);
      setTempPet({ category: '', total_fee: '', no_of_pets: '1' });
    } else if (editingPetIndex > index) {
      setEditingPetIndex(editingPetIndex - 1);
    }
  }, [pets, editingPetIndex, formik]);

  const updateTempPetField = useCallback((fieldName, value) => {
    if (fieldName === 'category' && value === 'N/A') {
      formik.setFieldValue('lease_details.pets', []);
      setEditingPetIndex(null);
      setTempPet({ category: '', total_fee: '', no_of_pets: '1' });
      return;
    }
    setTempPet((prev) => ({ ...prev, [fieldName]: value }));
  }, []);

  const commitPetChanges = useCallback(() => {
    if (editingPetIndex !== null) {
      const updatedPets = [...pets];
      updatedPets[editingPetIndex] = { ...tempPet };
      formik.setFieldValue('lease_details.pets', updatedPets);
      setIsAmountToBeChargedManuallyEdited(false);
    }
    setEditingPetIndex(null);
    setTempPet({ category: '', total_fee: '', no_of_pets: '1' });
  }, [editingPetIndex, tempPet, pets, formik]);

  const startEditingPet = useCallback((index) => {
    setEditingPetIndex(index);
    setTempPet({ ...pets[index] });
  }, [pets]);

  const addParking = useCallback(() => {
    if (editingParkingIndex !== null && tempParking) {
      const updatedParkingWithCurrentChanges = [...parking];
      updatedParkingWithCurrentChanges[editingParkingIndex] = { ...tempParking };
      const nextParkingType = getNextParkingType();
      const newParking = { parking_type: nextParkingType, total_fee: '', no_of_slots: '1' };
      const finalUpdatedParking = [...updatedParkingWithCurrentChanges, newParking];
      formik.setFieldValue('lease_details.parking', finalUpdatedParking);
      setEditingParkingIndex(finalUpdatedParking.length - 1);
      setTempParking(newParking);
    } else {
      const nextParkingType = getNextParkingType();
      const newParking = { parking_type: nextParkingType, total_fee: '', no_of_slots: '1' };
      const updatedParking = [...parking, newParking];
      formik.setFieldValue('lease_details.parking', updatedParking);
      setEditingParkingIndex(updatedParking.length - 1);
      setTempParking(newParking);
    }
  }, [parking, formik, getNextParkingType, editingParkingIndex, tempParking]);

  const removeParking = useCallback((index) => {
    const updatedParking = parking.filter((_, i) => i !== index);
    formik.setFieldValue('lease_details.parking', updatedParking);
    setIsAmountToBeChargedManuallyEdited(false);

    if (editingParkingIndex === index) {
      setEditingParkingIndex(null);
      setTempParking({ parking_type: '', total_fee: '', no_of_slots: '1' });
    } else if (editingParkingIndex > index) {
      setEditingParkingIndex(editingParkingIndex - 1);
    }
  }, [parking, editingParkingIndex, formik]);

  const updateTempParkingField = useCallback((fieldName, value) => {
    if (fieldName === 'parking_type' && value === 'N/A') {
      formik.setFieldValue('lease_details.parking', []);
      setEditingParkingIndex(null);
      setTempParking({ parking_type: '', total_fee: '', no_of_slots: '1' });
      return;
    }
    setTempParking((prev) => ({ ...prev, [fieldName]: value }));
  }, []);

  const commitParkingChanges = useCallback(() => {
    if (editingParkingIndex !== null) {
      const updatedParking = [...parking];
      updatedParking[editingParkingIndex] = { ...tempParking };
      formik.setFieldValue('lease_details.parking', updatedParking);
      setIsAmountToBeChargedManuallyEdited(false);
    }
    setEditingParkingIndex(null);
    setTempParking({ parking_type: '', total_fee: '', no_of_slots: '1' });
  }, [editingParkingIndex, tempParking, parking, formik]);

  const startEditingParking = useCallback((index) => {
    setEditingParkingIndex(index);
    setTempParking({ ...parking[index] });
  }, [parking]);

  const addStorage = useCallback(() => {
    if (editingStorageIndex !== null && tempStorage) {
      const updatedStorageWithCurrentChanges = [...storage];
      updatedStorageWithCurrentChanges[editingStorageIndex] = { ...tempStorage };

      const newStorage = { total_fee: '', no_of_slots: '1' };
      const finalUpdatedStorage = [...updatedStorageWithCurrentChanges, newStorage];

      formik.setFieldValue('lease_details.storage', finalUpdatedStorage);

      setEditingStorageIndex(finalUpdatedStorage.length - 1);
      setTempStorage(newStorage);
    } else {
      const newStorage = { total_fee: '', no_of_slots: '1' };
      const updatedStorage = [...storage, newStorage];
      formik.setFieldValue('lease_details.storage', updatedStorage);
      setEditingStorageIndex(updatedStorage.length - 1);
      setTempStorage(newStorage);
    }
  }, [storage, formik, editingStorageIndex, tempStorage]);

  const removeStorage = useCallback((index) => {
    const updatedStorage = storage.filter((_, i) => i !== index);
    formik.setFieldValue('lease_details.storage', updatedStorage);
    setIsAmountToBeChargedManuallyEdited(false);
    if (editingStorageIndex === index) {
      setEditingStorageIndex(null);
      setTempStorage({ total_fee: '', no_of_slots: '1' });
    } else if (editingStorageIndex > index) {
      setEditingStorageIndex(editingStorageIndex - 1);
    }
  }, [storage, editingStorageIndex, formik]);

  const updateTempStorageField = useCallback((fieldName, value) => {
    setTempStorage((prev) => ({ ...prev, [fieldName]: value }));
  }, []);

  const commitStorageChanges = useCallback(() => {
    if (editingStorageIndex !== null) {
      const updatedStorage = [...storage];
      updatedStorage[editingStorageIndex] = { ...tempStorage };
      formik.setFieldValue('lease_details.storage', updatedStorage);
      setIsAmountToBeChargedManuallyEdited(false);
    }
    setEditingStorageIndex(null);
    setTempStorage({ total_fee: '', no_of_slots: '1' });
  }, [editingStorageIndex, tempStorage, storage, formik]);

  const startEditingStorage = useCallback((index) => {
    setEditingStorageIndex(index);
    setTempStorage({ ...storage[index] });
  }, [storage]);

  const updateField = useCallback((fieldPath, value) => {
    let formattedValue = value;
    if (fieldPath === 'start_date' || fieldPath === 'end_date' || fieldPath === 'pro_ration.date_of_early_move_in') {
      formattedValue = value ? formatDateForStorage(value) : '';
    }
    formik.setFieldValue(`lease_details.${fieldPath}`, formattedValue);

    if (fieldPath === 'pro_ration.amount_to_be_charged') {
      setIsAmountToBeChargedManuallyEdited(true);
    }

    if (
      fieldPath === 'pro_ration.date_of_early_move_in' ||
      fieldPath === 'start_date' ||
      fieldPath === 'basic_rent' ||
      fieldPath === 'telus.included' ||
      fieldPath === 'telus.charges'
    ) {
      // Keep a deliberate "N/A" (charge set to 0) sticky when other inputs change.
      const currentCharge = formik.values.lease_details?.pro_ration?.amount_to_be_charged;
      if (Number(currentCharge) !== 0) {
        setIsAmountToBeChargedManuallyEdited(false);
      }
    }
  }, [formik]);

  const renderEditableField = (fieldKey, value, type = 'text', options = {}) => {
    const isEditing = editingField === fieldKey;
    const isCurrencyField = ['basic_rent', 'pro_ration.amount_to_be_charged', 'telus.charges', 'utilities.heat_fee', 'utilities.water_waste_fee', 'utilities.electricity_fee'].includes(fieldKey);

    if (isEditing) {
      switch (type) {
        case 'date':
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Input
                id={fieldKey}
                type="date"
                name={fieldKey}
                value={value ? value.split('T')[0] : ''}
                disabled={false}
                min={options.blockPastDates ? format(new Date(), 'yyyy-MM-dd') : undefined}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  const localDate = selectedDate ? parse(selectedDate, 'yyyy-MM-dd', new Date()) : null;
                  if (!options.blockPastDates || !localDate || !shouldDisableDate(localDate)) {
                    updateField(fieldKey, selectedDate);
                  }
                }}
                onBlur={() => setEditingField(null)}
                invalid={!!formik.errors?.lease_details?.[fieldKey.split('.')[1]]}
                sx={{
                  width: '100%',
                  padding: '8px',
                  border: formik.errors?.lease_details?.[fieldKey.split('.')[1]] ? '1px solid red' : '1px solid #ccc',
                  borderRadius: '4px',
                  '&:focus': {
                    borderColor: 'primary.main',
                    outline: 'none',
                  },
                }}
              />
            </Box>
          );
        case 'select':
          return (
            <FormControl fullWidth variant="outlined">
              <Select
                value={value || ''}
                onChange={(e) => {
                  const newValue = e.target.value;
                  updateField(fieldKey, newValue);
                  if (options.customOnChange) {
                    options.customOnChange(newValue);
                  }
                }}
                onBlur={() => setEditingField(null)}
                autoFocus
                displayEmpty
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: selected ? 'inherit' : 'text.secondary' }}>
                    {selected || 'Select an option'}
                  </Box>
                )}
                sx={{
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 2,
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 2,
                      mt: 1,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      '& .MuiMenuItem-root': {
                        borderRadius: 1,
                        mx: 1,
                        my: 0.5,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          }
                        }
                      }
                    }
                  }
                }}
              >
                {(!options.options || options.options.length === 0) && (
                  <MenuItem disabled>
                    <Typography color="text.secondary">No options available</Typography>
                  </MenuItem>
                )}
                {options.options?.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography>{option.label}</Typography>
                    {value === option.value && (
                      <CheckIcon sx={{ ml: 1, fontSize: 16, color: 'inherit' }} />
                    )}
                  </MenuItem>
                ))}
                {options.allowCustomValues && options.options?.length > 0 && (
                  <>
                    <Divider sx={{ my: 1, mx: 2 }} />
                    <Box sx={{ px: 3, pb: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5
                        }}
                      >
                        Custom Value
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Enter custom value..."
                          variant="outlined"
                          value={customIncentiveValue}
                          onChange={(e) => setCustomIncentiveValue(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (customIncentiveValue.trim()) {
                                updateField(fieldKey, customIncentiveValue.trim());
                                setCustomIncentiveValue('');
                                setEditingField(null);
                              }
                            }
                            if (e.key === 'Escape') {
                              setEditingField(null);
                            }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              backgroundColor: 'background.paper',
                            }
                          }}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          disabled={!customIncentiveValue.trim()}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (customIncentiveValue.trim()) {
                              updateField(fieldKey, customIncentiveValue.trim());
                              setCustomIncentiveValue('');
                              setEditingField(null);
                            }
                          }}
                          sx={{
                            whiteSpace: 'nowrap',
                            borderRadius: 1.5,
                            minWidth: 60,
                            textTransform: 'none',
                            fontWeight: 600
                          }}
                        >
                          Add
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
              </Select>
            </FormControl>
          );
        case 'checkbox':
          return (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!value}
                  onChange={(e) => {
                    updateField(fieldKey, e.target.checked);
                    if (options.customOnChange) {
                      options.customOnChange(e.target.checked);
                    }
                    setEditingField(null);
                  }}
                  autoFocus
                />
              }
              label={options.label || 'Included'}
            />
          );
        default:
          return (
            <TextField
              fullWidth
              variant="standard"
              value={value || ''}
              onChange={(e) => updateField(fieldKey, e.target.value)}
              onBlur={() => setEditingField(null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setEditingField(null);
                }
              }}
              autoFocus
              type={options.inputType || 'text'}
            />
          );
      }
    } else {
      const displayValue = () => {
        if (!value && value !== 0) {
          return `${options?.plainplaceholder ? options?.plainplaceholder : `Click to add ${options?.placeholder || 'value'}`}`;
        }

        switch (type) {
          case 'date':
            return formatDateForDisplay(value);
          case 'checkbox':
            return value ? 'Yes' : 'No';
          case 'select':
            const option = options.options?.find(opt => opt.value === value);
            return option ? option.label : value;
          default:
            return isCurrencyField ? `$${value}` : value;
        }
      };

      return (
        <Box
          onClick={() => setEditingField(fieldKey)}
          sx={{
            cursor: 'pointer',
            minHeight: '36px',
            display: 'flex',
            alignItems: 'center',
            flexGrow: 1,
            color: (!value && value !== 0) ? '#aaa' : 'inherit',
          }}
        >
          {displayValue()}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setEditingField(fieldKey);
            }}
            sx={{ ml: 1 }}
          >
            <EditIcon fontSize="small" color='error' />
          </IconButton>
        </Box>
      );
    }
  };

  const renderNonEditableField = (value, options = {}) => {
    const isCurrencyField = value !== '' && value !== false && !isNaN(parseFloat(value));

    return (
      <Box
        sx={{
          minHeight: '36px',
          display: 'flex',
          alignItems: 'center',
          flexGrow: 1,
          color: (!value && value !== 0) ? '#aaa' : 'inherit',
        }}
      >
        {value === false
          ? (options?.plainplaceholder || 'N/A')
          : isCurrencyField
            ? `$${value}`
            : value || options?.plainplaceholder || 'N/A'}
      </Box>
    );
  };

  const renderPetRows = () => {
    if (pets.length === 0) {
      return (
        <>
          <TableRow key="empty-state">
            <TableCell className="label">Pet</TableCell>
            <TableCell>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Box sx={{ minHeight: '36px', display: 'flex', alignItems: 'center', color: '#aaa' }}>
                    N/A
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ minHeight: '36px', display: 'flex', alignItems: 'center', color: '#aaa' }}>
                    N/A
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ minHeight: '36px', display: 'flex', alignItems: 'center', color: '#aaa' }}>
                    N/A
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton size="small" onClick={addPet} color="primary">
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            </TableCell>
          </TableRow>
          {showSaveMessage.pets && (
            <TableRow>
              <TableCell colSpan={2}>
                <Alert severity="warning">
                  Make sure you have clicked Done to save your changes for pets.
                </Alert>
              </TableCell>
            </TableRow>
          )}
        </>
      );
    }

    return (
      <>
        {pets.map((pet, index) => {
          const isEditing = editingPetIndex === index;
          return (
            <TableRow key={index}>
              <TableCell className="label">Pet {index + 1}</TableCell>
              <TableCell>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    {isEditing ? (
                      <FormControl fullWidth variant="standard">
                        <Select
                          value={tempPet.category || ''}
                          onChange={(e) => updateTempPetField('category', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">Select Category</MenuItem>
                          <MenuItem value="N/A">Not Applicable</MenuItem>
                          <MenuItem value="Dog">Dog</MenuItem>
                          <MenuItem value="Cat">Cat</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Box
                        onClick={() => startEditingPet(index)}
                        sx={{
                          cursor: 'pointer',
                          minHeight: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          color: pet.category ? 'inherit' : '#ccc',
                        }}
                      >
                        {pet.category || 'Select Category'}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={3}>
                    {isEditing ? (
                      <FormControl fullWidth variant="standard" size="small">
                        <Select
                          value={tempPet.no_of_pets || '1'}
                          onChange={(e) => updateTempPetField('no_of_pets', e.target.value)}
                          displayEmpty
                        >
                          {[...Array(7).keys()].map((i) => (
                            <MenuItem key={i + 1} value={String(i + 1)}>
                              {i + 1}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box
                        onClick={() => startEditingPet(index)}
                        sx={{
                          cursor: 'pointer',
                          minHeight: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          color: pet.no_of_pets ? 'inherit' : '#ccc',
                        }}
                      >
                        {pet.no_of_pets || '1'}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={3}>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        variant="standard"
                        value={tempPet.total_fee || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*\.?\d*$/.test(value) || value === '') {
                            updateTempPetField('total_fee', value);
                          }
                        }}
                        type="text"
                        placeholder="Total fee"
                      />
                    ) : (
                      <Box
                        onClick={() => startEditingPet(index)}
                        sx={{
                          cursor: 'pointer',
                          minHeight: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          color: pet.total_fee ? 'inherit' : '#ccc',
                        }}
                      >
                        ${pet.total_fee || '0'}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {isEditing ? (
                        <>
                          <IconButton size="small" onClick={commitPetChanges} color="success" sx={{ mr: 1 }}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => {
                            setEditingPetIndex(null);
                            setTempPet({ category: '', total_fee: '', no_of_pets: '1' });
                          }} color="warning" sx={{ mr: 1 }}>
                            <Cancel fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <IconButton size="small" onClick={() => startEditingPet(index)}>
                          <EditIcon fontSize="small" color='error' />
                        </IconButton>
                      )}
                      <IconButton size="small" onClick={() => removePet(index)} color="error" sx={{ mr: 1 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={addPet} color="primary">
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </TableCell>
            </TableRow>
          );
        })}
        {showSaveMessage.pets && (
          <TableRow>
            <TableCell colSpan={2}>
              <Alert severity="warning">
                Make sure you have clicked Done to save your changes for pets.
              </Alert>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  const renderParkingRows = () => {
    if (parking.length === 0) {
      return (
        <>
          <TableRow key="parking-empty-state">
            <TableCell className="label">Parking</TableCell>
            <TableCell>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Box sx={{ minHeight: '36px', display: 'flex', alignItems: 'center', color: '#aaa' }}>
                    N/A
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ minHeight: '36px', display: 'flex', alignItems: 'center', color: '#aaa' }}>
                    N/A
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ minHeight: '36px', display: 'flex', alignItems: 'center', color: '#aaa' }}>
                    N/A
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton size="small" onClick={addParking} color="primary">
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            </TableCell>
          </TableRow>
          {showSaveMessage.parking && (
            <TableRow>
              <TableCell colSpan={2}>
                <Alert severity="warning">
                  Make sure you have clicked Done to save your changes for parking.
                </Alert>
              </TableCell>
            </TableRow>
          )}
        </>
      );
    }

    return (
      <>
        {parking.map((parkingItem, index) => {
          const isEditing = editingParkingIndex === index;

          return (
            <TableRow key={index}>
              <TableCell className="label">Parking {index + 1}</TableCell>
              <TableCell>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    {isEditing ? (
                      <FormControl fullWidth variant="standard">
                        <Select
                          value={tempParking.parking_type || ''}
                          onChange={(e) => updateTempParkingField('parking_type', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">Select Type</MenuItem>
                          <MenuItem value="N/A">Not Applicable</MenuItem>
                          <MenuItem value="Surface Stall">Surface Stall</MenuItem>
                          <MenuItem value="Underground">Underground</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Box
                        onClick={() => startEditingParking(index)}
                        sx={{
                          cursor: 'pointer',
                          minHeight: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          color: parkingItem.parking_type ? 'inherit' : '#ccc',
                        }}
                      >
                        {parkingItem.parking_type || 'Select Type'}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={3}>
                    {isEditing ? (
                      <FormControl fullWidth variant="standard" size="small">
                        <Select
                          value={tempParking.no_of_slots || '1'}
                          onChange={(e) => updateTempParkingField('no_of_slots', e.target.value)}
                          displayEmpty
                        >
                          {[...Array(7).keys()].map((i) => (
                            <MenuItem key={i + 1} value={String(i + 1)}>
                              {i + 1}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box
                        onClick={() => startEditingParking(index)}
                        sx={{
                          cursor: 'pointer',
                          minHeight: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          color: parkingItem.no_of_slots ? 'inherit' : '#ccc',
                        }}
                      >
                        {parkingItem.no_of_slots || '1'}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={3}>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        variant="standard"
                        value={tempParking.total_fee || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*\.?\d*$/.test(value) || value === '') {
                            updateTempParkingField('total_fee', value);
                          }
                        }}
                        type="text"
                        placeholder="Total fee"
                      />
                    ) : (
                      <Box
                        onClick={() => startEditingParking(index)}
                        sx={{
                          cursor: 'pointer',
                          minHeight: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          color: parkingItem.total_fee ? 'inherit' : '#ccc',
                        }}
                      >
                        ${parkingItem.total_fee || '0'}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {isEditing ? (
                        <>
                          <IconButton size="small" onClick={commitParkingChanges} color="success" sx={{ mr: 1 }}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => {
                            setEditingParkingIndex(null);
                            setTempParking({ parking_type: '', total_fee: '', no_of_slots: '1' });
                          }} color="warning" sx={{ mr: 1 }}>
                            <Cancel fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <IconButton size="small" onClick={() => startEditingParking(index)}>
                          <EditIcon fontSize="small" color='error' />
                        </IconButton>
                      )}
                      <IconButton size="small" onClick={() => removeParking(index)} color="error" sx={{ mr: 1 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={addParking} color="primary">
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </TableCell>
            </TableRow>
          );
        })}
        {showSaveMessage.parking && (
          <TableRow>
            <TableCell colSpan={2}>
              <Alert severity="warning">
                Make sure you have clicked Done to save your changes for parking.
              </Alert>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  const renderStorageRows = () => {
    if (storage.length === 0) {
      return (
        <>
          <TableRow key="storage-empty-state">
            <TableCell className="label">Storage</TableCell>
            <TableCell>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box sx={{ minHeight: '36px', display: 'flex', alignItems: 'center', color: '#aaa' }}>
                    N/A
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ minHeight: '36px', display: 'flex', alignItems: 'center', color: '#aaa' }}>
                    N/A
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton size="small" onClick={addStorage} color="primary">
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            </TableCell>
          </TableRow>
          {showSaveMessage.storage && (
            <TableRow>
              <TableCell colSpan={2}>
                <Alert severity="warning">
                  Make sure you have clicked Done to save your changes for storage.
                </Alert>
              </TableCell>
            </TableRow>
          )}
        </>
      );
    }

    return (
      <>
        {storage.map((storageItem, index) => {
          const isEditing = editingStorageIndex === index;

          return (
            <TableRow key={index}>
              <TableCell className="label">Storage {index + 1}</TableCell>
              <TableCell>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    {isEditing ? (
                      <FormControl fullWidth variant="standard" size="small">
                        <Select
                          value={tempStorage.no_of_slots || '1'}
                          onChange={(e) => updateTempStorageField('no_of_slots', e.target.value)}
                          displayEmpty
                        >
                          {[...Array(7).keys()].map((i) => (
                            <MenuItem key={i + 1} value={String(i + 1)}>
                              {i + 1}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box
                        onClick={() => startEditingStorage(index)}
                        sx={{
                          cursor: 'pointer',
                          minHeight: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          color: storageItem.no_of_slots ? 'inherit' : '#ccc',
                        }}
                      >
                        {storageItem.no_of_slots || '1'}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        variant="standard"
                        value={tempStorage.total_fee || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*\.?\d*$/.test(value) || value === '') {
                            updateTempStorageField('total_fee', value);
                          }
                        }}
                        type="text"
                        placeholder="Total fee"
                      />
                    ) : (
                      <Box
                        onClick={() => startEditingStorage(index)}
                        sx={{
                          cursor: 'pointer',
                          minHeight: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          color: storageItem.total_fee ? 'inherit' : '#ccc',
                        }}
                      >
                        ${storageItem.total_fee || '0'}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {isEditing ? (
                        <>
                          <IconButton size="small" onClick={commitStorageChanges} color="success" sx={{ mr: 1 }}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => {
                            setEditingStorageIndex(null);
                            setTempStorage({ total_fee: '', no_of_slots: '1' });
                          }} color="warning" sx={{ mr: 1 }}
                          >
                            <Cancel fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <IconButton size="small" onClick={() => startEditingStorage(index)}>
                          <EditIcon fontSize="small" color='error' />
                        </IconButton>
                      )}
                      <IconButton size="small" onClick={() => removeStorage(index)} color="error" sx={{ mr: 1 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={addStorage} color="primary">
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </TableCell>
            </TableRow>
          );
        })}
        {showSaveMessage.storage && (
          <TableRow>
            <TableCell colSpan={2}>
              <Alert severity="warning">
                Make sure you have clicked Done to save your changes for storage.
              </Alert>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  return (
    <Paper variant="outlined" className="lease-details-table" sx={{ mt: 2 }}>
      <Table size="small">
        <TableBody>
          <TableRow>
            <TableCell className="label">Start Date - End Date</TableCell>
            <TableCell>
              <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
                <div style={{ flex: '1' }}>
                  {renderEditableField('start_date', leaseDetails.start_date, 'date', {
                    blockPastDates: true,
                    placeholder: 'lease start date'
                  })}
                </div>
                <div style={{ flex: '1' }}>
                  {renderEditableField('end_date', leaseDetails.end_date, 'date', {
                    blockPastDates: true,
                    placeholder: 'lease end date'
                  })}
                </div>
              </div>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="label">Monthly Basic Rent</TableCell>
            <TableCell>
              {renderEditableField('basic_rent', leaseDetails.basic_rent, 'text', {
                inputType: 'number',
                placeholder: 'monthly rent amount'
              })}
            </TableCell>
          </TableRow>
          {renderPetRows()}
          {renderParkingRows()}
          {renderStorageRows()}
          {UTILITY_ROWS.map(({ key, feeField, label }) => {
            const tenantPays = formik.values.property_details?.utilities_available?.[key] === true;
            const fee = parseFloat(leaseDetails.utilities?.[feeField] || 0);
            if (!tenantPays && !(fee > 0)) return null;
            return (
              <TableRow key={key}>
                <TableCell className="label">{label}</TableCell>
                <TableCell>
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
                    <div style={{ flex: '1' }}>
                      {renderEditableField(`utilities.${feeField}`, leaseDetails.utilities?.[feeField], 'text', {
                        inputType: 'number',
                        placeholder: 'monthly fee',
                      })}
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          <TableRow>
            <TableCell style={{ backgroundColor: '#d4dcf3', padding: '0.7rem' }} className="label">
              TOTAL MONTHLY RENT:
            </TableCell>
            <TableCell style={{ backgroundColor: '#d4dcf3', padding: '0.7rem' }}>
              {calculateTotalRent()}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="label">Pro-Ration Details</TableCell>
            <TableCell>
              <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
                <div style={{ flex: '1' }}>
                  {renderEditableField('pro_ration.date_of_early_move_in', leaseDetails.pro_ration?.date_of_early_move_in, 'date', {
                    plainplaceholder: 'Early Move-In'
                  })}
                  <small>Early Move-In Date</small>
                </div>
                <div style={{ flex: '1' }}>
                  {renderNonEditableField(proRationData.amount_applicable, {
                    plainplaceholder: 'Prorated Amt.'
                  })}
                  <small>Prorated Amount</small>
                </div>
                <div style={{ flex: '1' }}>
                  {renderEditableField('pro_ration.amount_to_be_charged', leaseDetails.pro_ration?.amount_to_be_charged, 'text', {
                    inputType: 'number',
                    plainplaceholder: 'Actual Amt.'
                  })}
                  <small>Actual Amount</small>
                </div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.lease_details?.pro_ration?.amount_to_be_charged === 0}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          handleNoCharge();
                        } else {
                          formik.setFieldValue('lease_details.pro_ration.amount_to_be_charged', proRationData.amount_to_be_charged);
                          setIsAmountToBeChargedManuallyEdited(false);
                        }
                      }}
                      aria-label="No Charge"
                    />
                  }
                  label="N/A"
                />
              </div>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="label">Telus</TableCell>
            <TableCell>
              <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
                <div style={{ flex: '1' }}>
                  {renderEditableField('telus.included', leaseDetails.telus?.included, 'checkbox', {
                    label: 'Included',
                    plainplaceholder: 'Is telus Included',
                    customOnChange: (value) => {
                      if (value) {
                        formik.setFieldValue('lease_details.telus.charges', 0);
                      }
                    }
                  })}
                </div>
                {!leaseDetails.telus?.included && (
                  <div style={{ flex: '1' }}>
                    {renderEditableField('telus.charges', leaseDetails.telus?.charges, 'text', {
                      inputType: 'number',
                      placeholder: 'charges'
                    })}
                  </div>
                )}
              </div>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="label">Incentives</TableCell>
            <TableCell>
              {renderEditableField('incentives', leaseDetails.incentives, 'select', {
                allowCustomValues: true,
                options: incentiveOptions.map((item) => ({ value: item, label: item })),
                placeholder: 'incentives'
              })}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table> 
    </Paper>
  );
};

const Step1 = ({ formik }) => {
  const { property_id } = useParams();
  const [incentiveOptions, setIncentiveOptions] = useState([]);

  useEffect(() => {
    const fetchProperty = async () => {
      const { data } = await axios.get(`property/${property_id}`);
      setIncentiveOptions(data.property.incentives);
    };

    if (property_id) {
      fetchProperty();
    }
  }, [property_id]);

  return (
    <Box className="lease-form-step1">
      <Typography variant="h6" align="center" gutterBottom className="form-title">
        RESIDENTIAL TENANCY AGREEMENT
      </Typography>
      <Typography variant="body2" align="center" className="subtitle">
        (made in duplicate) Between
      </Typography>
      <Typography variant="body1" align="center" className="landlord-name" sx={{ mt: 2, mb: 3 }}>
        {formik.values.property_details?.partnership_label || 'N/A'} / {formik.values.property_details?.landlord || 'N/A'} (the "Landlord") – and –
      </Typography>
      <Tenants
        formik={formik}
        fieldName="tenants"
        options={{
          minItems: 1,
          maxItems: 4,
          emptyText: 'No tenants added',
          paperProps: {
            variant: 'outlined',
            className: 'unit-details-table',
          },
        }}
      />
      <Typography variant="body2" align="center" className="tenant-reference" sx={{ mt: 1 }}>
        (Individually or collectively referred to as the "Tenant")
      </Typography>
      <Typography variant="body2" align="center" className="agreement-note" sx={{ mt: 1, mb: 1 }}>
        THE TENANCY CREATED BY THIS AGREEMENT IS GOVERNED BY THE <b>RESIDENTIAL TENANCIES ACT</b> (THE "ACT"),
        AND IF THERE IS A CONFLICT BETWEEN THE AGREEMENT AND THE ACT, THE ACT PREVAILS.
      </Typography>
      <Typography variant="body1" component="div" className="lease-text">
        <b>PREMISES:</b> The Landlord hereby leases to the Tenant and the Tenant hereby leases from the Landlord the following residential premises described as follows;
      </Typography>
      <Fields
        formik={formik}
        fieldArrayName="property_details"
        fields={[
          { name: 'property_name', label: 'Building', type: 'text', readOnly: true },
          { name: 'unit_no', label: 'Unit Number', type: 'text', readOnly: true },
          { name: 'address', label: 'Address', type: 'text', readOnly: false },
          {
            label: 'City, Province',
            type: 'group',
            groupLabel: 'City, Province',
            fields: [
              { name: 'city', label: 'City', type: 'text', readOnly: true },
              { name: 'province', label: 'Province', type: 'text', readOnly: true },
            ],
          },
          { name: 'postal_code', label: 'Postal Code', type: 'text', readOnly: false },
        ]}
        options={{
          minItems: 1,
          maxItems: 1,
          showAddButtonInRow: false,
          paperProps: { variant: 'outlined', className: 'unit-details-table' },
        }}
        isObject={true}
      />
      <Box className="section">
        <Typography variant="subtitle2" className="section-title">1. TERM & NOTICE</Typography>
        <Typography variant="body2" className="section-content">
          This agreement is for a fixed term, commencing at 12 o'clock noon on{' '}
          <span
            style={{
              fontWeight: 600,
              color: '#1976d2',
            }}
          >
            {isValid(new Date(formik.values.lease_details?.start_date))
              ? formatDateWithOrdinal(formik.values.lease_details.start_date)
              : '___'}
          </span>
          , ending at 12 o'clock noon on the{' '}
          <span
            style={{
              fontWeight: 600,
              color: '#1976d2',
            }}
          >
            {isValid(new Date(formik.values.lease_details?.end_date))
              ? formatDateWithOrdinal(formik.values.lease_details.end_date)
              : '___'}
          </span>
        </Typography>

      </Box>
      <Box className="section">
        <Typography variant="subtitle2" className="section-title">2. RENT</Typography>
        <Typography variant="body2" className="section-content">
          The tenant shall pay to the landlord without any deductions whatsoever as the landlord shall direct
          from time to time a monthly rental sum [hereafter called "rent"] of:
        </Typography>
      </Box>
      <LeaseDetails
        formik={formik}
        incentiveOptions={incentiveOptions}
      />
      <Box className="caption">
        <Typography variant="body2" className="caption">
          Rent is due in full no later than the 1<sup>st</sup> day of each month. <br />
          When two or more persons comprise the tenant for the purposes of this agreement, the landlord may collect the rent due to the landlord pursuant to the agreement from anyone, some or all of them. The tenant shall pay the rent reserved, without demand, at the times and in the manner specified here in provided without deduction, abatement or set-off.
        </Typography>
      </Box>

      <Box className="section">
        <Typography variant="subtitle2" className="section-title">3. NOTICE OF EARLY LEASE TERMINATION</Typography>
        <Typography variant="body2" className="section-content" sx={{ backgroundColor: '#ffff99', p: 1, borderRadius: 1 }}>
          Subject to termination in accordance with this Agreement or the Act, the Tenant ends
          the fixed-term tenancy prior to the Expiry Date, the Landlord may treat this Agreement
          as abandoned in accordance with the Act. In such an event, the Tenant shall pay to the
          Landlord a lease break fee equivalent to two (2) months' rent. This amount is payable
          in addition to any other amounts owed by the Tenant, including but not limited to unpaid
          rent, or the reasonable cost of repairing any damage to the Premises or Residential
          Property (excluding fair wear and tear).
        </Typography>
      </Box>
    </Box>
  );
};

export { Step1 };